import React, { useState, useEffect, useMemo } from 'react';
import { X, Save, AlertCircle, CheckCircle, Trash2 } from 'lucide-react';
import { FilterGroupDto, UpdateFilterGroupRequest } from '../../../../types/filterGroup';
import { filterGroupService } from '../../../../services/filterGroupService';

interface SelectedRows {
  [key: string]: boolean;
}

interface FilterGroupBulkUpdateModalProps {
  selectedRows: SelectedRows;
  data: FilterGroupDto[];
  onClose: () => void;
  onSave: () => void;
}

interface BulkEditConfig {
  [key: string]: {
    enabled: boolean;
    value: any;
  };
}

type FieldType = 'text' | 'number';

interface FieldDefinition {
  key: string;
  label: string;
  type: FieldType;
  singleOnly?: boolean;
}

export const FilterGroupBulkUpdateModal: React.FC<FilterGroupBulkUpdateModalProps> = ({
  selectedRows,
  data,
  onClose,
  onSave
}) => {
  const [bulkEditConfig, setBulkEditConfig] = useState<BulkEditConfig>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'edit' | 'delete'>('edit');

  const selectedRowCount = Object.values(selectedRows).filter(Boolean).length;
  const selectedData = data.filter(item => selectedRows[String(item.id)]);
  const isSingleSelection = selectedRowCount === 1;
  const singleItem = isSingleSelection ? selectedData[0] : null;

  const fields: FieldDefinition[] = useMemo(() => [
    { key: 'id', label: 'ID', type: 'number', singleOnly: true },
    { key: 'filterGroupID', label: 'Filtergruppe ID', type: 'text', singleOnly: true },
    { key: 'filterGroupDescr', label: 'Beskrivelse', type: 'text' },
    { key: 'filterGroupSortID', label: 'Sortering', type: 'number' }
  ], []);

  const getDefaultValue = (field: FieldDefinition): any => {
    if (field.type === 'text') return '';
    if (field.type === 'number') return undefined;
    return '';
  };

  useEffect(() => {
    const initialConfig: BulkEditConfig = {};
    fields.forEach(field => {
      if (isSingleSelection && singleItem) {
        initialConfig[field.key] = {
          enabled: true,
          value: (singleItem as any)[field.key] ?? getDefaultValue(field)
        };
      } else {
        initialConfig[field.key] = {
          enabled: false,
          value: getDefaultValue(field)
        };
      }
    });
    setBulkEditConfig(initialConfig);
  }, []);

  const handleFieldToggle = (fieldKey: string, enabled: boolean) => {
    setBulkEditConfig(prev => ({
      ...prev,
      [fieldKey]: {
        ...prev[fieldKey],
        enabled
      }
    }));
  };

  const handleFieldValueChange = (field: FieldDefinition, value: any) => {
    let parsedValue = value;
    if (field.type === 'number') {
      parsedValue = value !== '' && value !== undefined && value !== null ? parseInt(value) : undefined;
    }
    setBulkEditConfig(prev => ({
      ...prev,
      [field.key]: {
        ...prev[field.key],
        value: parsedValue
      }
    }));
  };

  const handleBulkUpdate = async () => {
    try {
      setIsSaving(true);
      setError('');

      const enabledFields = Object.entries(bulkEditConfig)
        .filter(([, config]) => config.enabled)
        .map(([field, config]) => ({ field, value: config.value }));

      if (enabledFields.length === 0) {
        setError('Ingen felter valgt for oppdatering');
        return;
      }

      // Sjekk singleOnly-felter
      const singleOnlyFields = fields.filter(f => f.singleOnly);
      const singleOnlyEnabled = enabledFields.filter(ef =>
        singleOnlyFields.some(sf => sf.key === ef.field)
      );
      if (singleOnlyEnabled.length > 0 && selectedRowCount > 1) {
        setError(`${singleOnlyEnabled.map(f => f.field).join(', ')} kan kun endres for én rad om gangen`);
        return;
      }

      for (const item of selectedData) {
        const request: UpdateFilterGroupRequest = {
          filterGroupDescr: item.filterGroupDescr,
          filterGroupSortID: item.filterGroupSortID
        };

        enabledFields.forEach(({ field, value }) => {
          (request as any)[field] = value;
        });

        await filterGroupService.update(
          item.companyNo,
          item.filterGroupID,
          item.filterGroupType,
          request
        );
      }

      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        onSave();
      }, 1500);
    } catch (err: any) {
      setError(err.message || 'Feil ved oppdatering');
    } finally {
      setIsSaving(false);
    }
  };

  const handleBulkDelete = async () => {
    try {
      setIsDeleting(true);
      setError('');

      let deletedCount = 0;
      for (const item of selectedData) {
        await filterGroupService.delete(item.companyNo, item.filterGroupID, item.filterGroupType);
        deletedCount++;
      }

      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        onSave();
      }, 1500);
    } catch (err: any) {
      setError(err.message || 'Feil ved sletting');
    } finally {
      setIsDeleting(false);
    }
  };

  const enabledFieldCount = Object.values(bulkEditConfig).filter(config => config.enabled).length;

  const renderFieldInput = (field: FieldDefinition, config: { enabled: boolean; value: any }) => {
    if (!config.enabled) return null;

    if (field.type === 'number') {
      return (
        <div className="mt-2">
          <input
            type="number"
            value={config.value ?? ''}
            onChange={(e) => handleFieldValueChange(field, e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder={field.label}
          />
        </div>
      );
    }

    return (
      <div className="mt-2">
        <input
          type="text"
          value={config.value ?? ''}
          onChange={(e) => handleFieldValueChange(field, e.target.value)}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder={field.label}
        />
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-25 transition-opacity" onClick={onClose} />

      {/* Right Side Panel */}
      <div className="absolute right-0 top-0 h-full w-96 bg-white shadow-xl border-l border-gray-200 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {isSingleSelection ? 'Rediger filtergruppe' : 'Masseoppdatering'}
            </h2>
            <p className="text-sm text-gray-600">
              {selectedRowCount} {selectedRowCount === 1 ? 'rad' : 'rader'} valgt i Filtergrupper
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('edit')}
            className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === 'edit'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Rediger
          </button>
          <button
            onClick={() => setActiveTab('delete')}
            className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === 'delete'
                ? 'text-red-600 border-b-2 border-red-600 bg-red-50'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Slett
          </button>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}

          {activeTab === 'edit' ? (
            <>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  {isSingleSelection
                    ? 'Rediger feltene nedenfor for den valgte filtergruppen.'
                    : 'Aktiver feltene du vil oppdatere. Verdien settes for alle valgte rader.'}
                </p>
              </div>

              <div className="space-y-3">
                <h3 className="text-sm font-medium text-gray-900">Felter som skal oppdateres</h3>

                {fields.map(field => {
                  const config = bulkEditConfig[field.key];
                  if (!config) return null;

                  return (
                    <div
                      key={field.key}
                      className={`border rounded-lg p-3 transition-colors ${
                        config.enabled ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-white'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={config.enabled}
                            onChange={(e) => handleFieldToggle(field.key, e.target.checked)}
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <span className="text-sm font-medium text-gray-700">
                            {field.label}
                          </span>
                        </label>
                        {field.singleOnly && selectedRowCount > 1 && config.enabled && (
                          <span className="text-xs text-amber-600">Kun ved enkeltvalg</span>
                        )}
                      </div>
                      {renderFieldInput(field, config)}
                    </div>
                  );
                })}
              </div>

              {enabledFieldCount > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-green-800">
                      <p className="font-medium">Klar til å lagre</p>
                      <p>
                        {enabledFieldCount} felt vil bli oppdatert for {selectedRowCount} {selectedRowCount === 1 ? 'rad' : 'rader'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-red-800">
                    <p className="font-medium">Advarsel</p>
                    <p>
                      Du er i ferd med å slette {selectedRowCount} filtergruppe{selectedRowCount === 1 ? '' : 'r'}.
                      Denne handlingen kan ikke angres.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <h3 className="text-sm font-medium text-gray-900">Filtergrupper som vil bli slettet:</h3>
                {selectedData.map(item => (
                  <div key={item.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded border border-gray-200">
                    <span className="text-sm font-medium text-gray-700">{item.filterGroupID}</span>
                    <span className="text-xs text-gray-500">Type {item.filterGroupType}</span>
                    {item.filterGroupDescr && (
                      <span className="text-xs text-gray-400">— {item.filterGroupDescr}</span>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Footer Actions */}
        <div className="border-t border-gray-200 p-4 bg-gray-50">
          <div className="flex items-center justify-between gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Avbryt
            </button>

            {activeTab === 'edit' ? (
              <button
                onClick={handleBulkUpdate}
                disabled={enabledFieldCount === 0 || isSaving}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  enabledFieldCount === 0 || isSaving
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {isSaving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Lagrer...
                  </>
                ) : showSuccess ? (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Lagret!
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Lagre endringer
                  </>
                )}
              </button>
            ) : (
              <button
                onClick={handleBulkDelete}
                disabled={isDeleting}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  isDeleting
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-red-600 text-white hover:bg-red-700'
                }`}
              >
                {isDeleting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Sletter...
                  </>
                ) : showSuccess ? (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Slettet!
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Slett {selectedRowCount} {selectedRowCount === 1 ? 'rad' : 'rader'}
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
